import type { RichCard } from "./agent-demo-data"

/** Tool call item for skill demo (simplified MainToolCall) */
export interface SkillDemoToolCall {
  label: string
  paramLabel?: string
  result?: string
}

/** Skill demo conversation step — extended to support rich content */
export interface SkillDemoStep {
  type: "user" | "agent-reply" | "tool-calls" | "rich-card"
  content?: string
  delay: number
  /** For tool-calls type */
  toolCalls?: SkillDemoToolCall[]
  /** For rich-card type */
  richCards?: RichCard[]
}

// ── Per-skill demo data, keyed by lowercase skill name ──

const OBSIDIAN_DEMO: SkillDemoStep[] = [
  { type: "user", content: "我之前记过一篇关于 OKR 写法的笔记，帮我找出来，顺便整理成这个季度的模板", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "定位 Obsidian 库", paramLabel: "~/Documents/MyVault", result: "已连接" },
      { label: "搜索笔记内容", paramLabel: '"OKR"', result: "命中 3 篇" },
      { label: "读取笔记", paramLabel: "学习笔记/OKR最佳实践.md", result: "2400 字" },
    ],
  },
  {
    type: "rich-card", delay: 4500,
    richCards: [{
      type: "deliverables", title: "找到的笔记",
      files: [
        { name: "OKR最佳实践.md", type: "md", size: "学习笔记/" },
        { name: "Q3复盘.md", type: "md", size: "工作记录/" },
        { name: "管理方法论汇总.md", type: "md", size: "读书笔记/" },
      ],
    }],
  },
  {
    type: "agent-reply", delay: 5500,
    content: "找到了，你去年 8 月记的那篇最完整。我根据里面的框架帮你生成了 Q2 模板，已经存到你的笔记库里了：\n\n新建笔记 → 工作记录/2026-Q2-OKR模板.md",
  },
]

const WHOOP_DEMO: SkillDemoStep[] = [
  { type: "user", content: "早上好，今天身体状态怎么样？", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "拉取 WHOOP 数据", paramLabel: "恢复/睡眠/运动压力", result: "已获取" },
    ],
  },
  {
    type: "rich-card", delay: 3000,
    richCards: [{
      type: "table", title: "今日健康报告",
      columns: [
        { key: "metric", label: "指标" },
        { key: "value", label: "数值" },
        { key: "status", label: "状态" },
      ],
      rows: [
        { metric: "恢复指数", value: "78%", status: "良好" },
        { metric: "睡眠时长", value: "7h12m（需 7h30m）", status: "略欠" },
        { metric: "睡眠质量", value: "82%", status: "良好" },
        { metric: "昨日运动压力", value: "14.2", status: "中高" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 4200,
    richCards: [{
      type: "alert", title: "今日建议",
      items: [
        { level: "safe", label: "恢复状态良好，可以正常训练" },
        { level: "warning", label: "睡眠差 18 分钟，今晚建议提前半小时上床" },
        { level: "warning", label: "昨天运动强度偏高，今天建议轻度有氧（慢跑/瑜伽）" },
      ],
    }],
  },
  { type: "agent-reply", delay: 5500, content: "整体状态不错，但连续两天高强度了，今天建议上轻度有氧，别硬撑。" },
]

const SUMMARIZE_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我总结一下这个视频在讲什么 https://youtu.be/dQw4w9WgXcQ", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "解析 YouTube 链接", paramLabel: "提取字幕/元信息", result: "时长 3:32" },
      { label: "AI 总结", paramLabel: "生成摘要", result: "完成" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "deliverables", title: "内容摘要",
      files: [
        { name: "视频摘要", type: "txt", size: "约 200 字" },
        { name: "关键词：承诺、信任、永不放弃", type: "tag" },
      ],
    }],
  },
  {
    type: "agent-reply", delay: 5000,
    content: "这是 Rick Astley 的经典 MV《Never Gonna Give You Up》，核心内容就一句话：我永远不会让你失望。恭喜，你被 Rickroll 了\n\n开个玩笑——总结功能支持任何网页、PDF、音频和视频，丢个正经链接进来试试？",
  },
]

const NOTION_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我看看 Notion 里这周有哪些任务还没完成", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "连接 Notion 工作区", paramLabel: "My Workspace", result: "已连接" },
      { label: "查询任务数据库", paramLabel: "筛选 Status ≠ Done", result: "6 条待办" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "table", title: "本周未完成任务",
      columns: [
        { key: "task", label: "任务" },
        { key: "priority", label: "优先级" },
        { key: "due", label: "截止日期" },
        { key: "status", label: "状态" },
      ],
      rows: [
        { task: "写 Q2 营销方案", priority: "高", due: "3月17日", status: "进行中" },
        { task: "更新产品定价页", priority: "中", due: "3月18日", status: "待开始" },
        { task: "竞品分析报告", priority: "中", due: "3月19日", status: "进行中" },
        { task: "整理用户反馈", priority: "低", due: "3月20日", status: "待开始" },
        { task: "团建方案确认", priority: "低", due: "3月21日", status: "待开始" },
        { task: "周报", priority: "中", due: "3月21日", status: "待开始" },
      ],
    }],
  },
  { type: "agent-reply", delay: 5500, content: '6 条没完成，其中「Q2 营销方案」最紧急，后天就到期了。要我帮你把"更新产品定价页"标记为进行中吗？' },
]

const GOG_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我看看明天有什么会，顺便查一下小王上周发的那封关于报价的邮件", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "查询 Google 日历", paramLabel: "明天全天", result: "3 场会议" },
      { label: "搜索 Gmail", paramLabel: 'from:小王 + "报价"', result: "1 封命中" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "table", title: "明日日程",
      columns: [
        { key: "time", label: "时间" },
        { key: "meeting", label: "会议" },
        { key: "people", label: "参会人" },
      ],
      rows: [
        { time: "09:30-10:00", meeting: "周会", people: "全组" },
        { time: "14:00-15:00", meeting: "客户方案评审", people: "你、小王、客户方" },
        { time: "16:00-16:30", meeting: "1:1 周期会", people: "你、主管" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 5000,
    richCards: [{
      type: "deliverables", title: "邮件匹配",
      files: [
        { name: "Re: Q2 报价方案 v2", type: "email", size: "来自 小王，3月10日" },
      ],
    }],
  },
  { type: "agent-reply", delay: 6200, content: "明天 3 场会。2 点的客户评审跟小王那封报价邮件有关——他 3月10日 发的 v2 版本，要我把邮件内容展开看看吗？" },
]

const KALSHI_DEMO: SkillDemoStep[] = [
  { type: "user", content: "最近市场上大家都在关注什么？有没有高确定性的机会", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "获取 Kalshi 热门市场", paramLabel: "按成交量排序", result: "12 个活跃市场" },
      { label: "筛选高确定性机会", paramLabel: "置信度≥85% + 回报率≥10%", result: "3 个命中" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "table", title: "热门市场概览",
      columns: [
        { key: "market", label: "市场" },
        { key: "price", label: "当前价格" },
        { key: "volume", label: "成交量" },
        { key: "direction", label: "方向" },
      ],
      rows: [
        { market: "美联储 6 月降息", price: "YES 72¢", volume: "$2.1M", direction: "看多" },
        { market: "BTC 年底破 $15万", price: "YES 38¢", volume: "$1.8M", direction: "分歧" },
        { market: "某科技股 Q2 超预期", price: "YES 89¢", volume: "$950K", direction: "高度看多" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 5200,
    richCards: [{
      type: "alert", title: "高确定性机会",
      items: [
        { level: "safe", label: "某科技股 Q2 超预期", detail: "YES 89¢，潜在回报 12%" },
        { level: "safe", label: "6月前不加息", detail: "YES 91¢，潜在回报 10%" },
        { level: "warning", label: "美联储 6 月降息", detail: "YES 72¢，回报高但确定性不足" },
      ],
    }],
  },
  { type: "agent-reply", delay: 6500, content: "当前市场最热的是美联储政策走向。高确定性里，「某科技股 Q2 超预期」（89%置信度）和「6月前不加息」（91%）最稳。注意：这是只读分析，不会帮你下单。" },
]

const MISSION_CONTROL_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我看看任务板上现在什么情况", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "读取任务面板", paramLabel: "data/tasks.json", result: "已加载" },
    ],
  },
  {
    type: "rich-card", delay: 2800,
    richCards: [{
      type: "table", title: "任务看板",
      columns: [
        { key: "task", label: "任务" },
        { key: "priority", label: "优先级" },
        { key: "status", label: "状态" },
        { key: "subtasks", label: "子任务" },
      ],
      rows: [
        { task: "重构登录模块", priority: "高", status: "进行中", subtasks: "2/4 完成" },
        { task: "编写 API 文档", priority: "中", status: "待办", subtasks: "0/3 完成" },
        { task: "修复搜索 bug", priority: "高", status: "审核中", subtasks: "3/3 完成" },
        { task: "优化首页加载速度", priority: "中", status: "待办", subtasks: "0/2 完成" },
      ],
    }],
  },
  { type: "agent-reply", delay: 4500, content: "「重构登录模块」正在进行中，已完成一半。「修复搜索 bug」做完了等你审核——你在面板上把它拖到「已完成」我就结项。要我继续推进登录模块的下一个子任务吗？" },
]

const XEROLITE_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我买 10 股苹果", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "搜索合约", paramLabel: "AAPL / 美股 / SMART", result: "Apple Inc. (AAPL)" },
      { label: "下达订单", paramLabel: "BUY 10 股 AAPL", result: "订单已提交" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "table", title: "订单确认",
      columns: [
        { key: "item", label: "项目" },
        { key: "detail", label: "详情" },
      ],
      rows: [
        { item: "标的", detail: "Apple Inc. (AAPL)" },
        { item: "方向", detail: "买入" },
        { item: "数量", detail: "10 股" },
        { item: "交易所", detail: "SMART（最优路由）" },
        { item: "状态", detail: "已提交" },
      ],
    }],
  },
  { type: "agent-reply", delay: 5500, content: "10 股苹果的买单已经提交到 Interactive Brokers，SMART 交易所自动选最优价格。订单成交后你会在 TWS 里看到持仓更新。" },
]

const TAVILY_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我调研一下 2026 年最好的开源 LLM 框架有哪些", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "Tavily 搜索", paramLabel: '"best open source LLM framework 2026"', result: "8 条结果" },
      { label: "Tavily 搜索", paramLabel: '"LLM 框架对比 2026" 限定 github.com', result: "5 条结果" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "deliverables", title: "信息源",
      files: [
        { name: "2026 LLM Framework Comparison", type: "link", size: "github.com" },
        { name: "State of Open Source AI", type: "link", size: "huggingface.co" },
        { name: "框架选型指南", type: "link", size: "arxiv.org" },
        { name: "实测对比：vLLM vs TGI vs...", type: "link", size: "benchmark-blog.dev" },
      ],
    }],
  },
  { type: "agent-reply", delay: 5500, content: "帮你从 13 个信息源里筛出了 4 个最有价值的。GitHub 那篇对比表最全，HuggingFace 的年度报告有行业趋势。要我把这些内容总结成一份调研摘要吗？" },
]

const SKILL_VETTER_DEMO: SkillDemoStep[] = [
  { type: "user", content: '我想装一个叫 "quick-deploy" 的技能，帮我先看看安全不安全', delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "查找技能来源", paramLabel: "ClawdHub / quick-deploy", result: "已定位" },
      { label: "审查代码文件", paramLabel: "6 个文件逐一检查", result: "审查完毕" },
      { label: "权限范围评估", paramLabel: "文件/网络/命令", result: "已评估" },
    ],
  },
  {
    type: "rich-card", delay: 4500,
    richCards: [{
      type: "alert", title: "安全审核报告",
      items: [
        { level: "safe", label: "来源", detail: "ClawdHub 官方收录，作者 1.2k 星" },
        { level: "safe", label: "代码量", detail: "6 个文件，均可读无混淆" },
        { level: "danger", label: "风险", detail: "deploy.sh 中有 curl 请求访问外部服务器 api.quick-deploy.io" },
        { level: "warning", label: "注意", detail: "需要读取 ~/.ssh/config（用于部署密钥）" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 5800,
    richCards: [{
      type: "table", title: "审核结论",
      columns: [
        { key: "dimension", label: "维度" },
        { key: "result", label: "结果" },
      ],
      rows: [
        { dimension: "风险等级", result: "中风险" },
        { dimension: "判定", result: "谨慎安装" },
        { dimension: "原因", result: "需访问外网 + 读取 SSH 配置" },
      ],
    }],
  },
  { type: "agent-reply", delay: 7000, content: "这个技能功能正常，但它会访问外部服务器并读取你的 SSH 配置，属于「中风险」。如果你确认信任 quick-deploy.io 这个服务，可以装；不确定的话建议先不装。" },
]

const FAST_IO_DEMO: SkillDemoStep[] = [
  { type: "user", content: "我刚做完一份竞品分析报告，帮我分享给客户", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "上传文件", paramLabel: '竞品分析报告.pdf → 工作区 "项目A"', result: "已上传" },
      { label: "创建分享链接", paramLabel: "Send / 7天有效 / 需密码", result: "已生成" },
      { label: "记录操作日志", paramLabel: "worklog", result: "已记录" },
    ],
  },
  {
    type: "rich-card", delay: 4500,
    richCards: [{
      type: "deliverables", title: "分享已就绪",
      files: [
        { name: "分享链接 — share.fast.io/abc123", type: "link" },
        { name: "访问密码 — Xk92mP", type: "key" },
        { name: "有效期 — 7 天", type: "info" },
      ],
    }],
  },
  { type: "agent-reply", delay: 5800, content: '报告已上传到"项目A"工作区并生成了分享链接。客户打开链接输入密码就能在线预览和下载，不需要注册。链接 7 天后自动失效。' },
]

const N8N_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我搞一个自动化：每周一早上把上周的销售数据从表格里汇总，发邮件给老板", delay: 0 },
  {
    type: "tool-calls", delay: 1500,
    toolCalls: [
      { label: "设计触发器", paramLabel: "Cron: 每周一 08:00" },
      { label: "设计数据流", paramLabel: "表格读取 → 聚合 → 邮件发送" },
      { label: "添加错误处理", paramLabel: "失败重试 + 告警通知" },
      { label: "生成工作流 JSON", paramLabel: "workflow.json" },
    ],
  },
  {
    type: "rich-card", delay: 5000,
    richCards: [{
      type: "table", title: "工作流设计",
      columns: [
        { key: "step", label: "步骤" },
        { key: "node", label: "节点" },
        { key: "desc", label: "说明" },
      ],
      rows: [
        { step: "1", node: "定时触发", desc: "每周一 08:00" },
        { step: "2", node: "读取表格", desc: "Google Sheets 上周数据" },
        { step: "3", node: "数据汇总", desc: "按产品线聚合销售额" },
        { step: "4", node: "发送邮件", desc: "发给老板，附汇总表" },
        { step: "5", node: "失败重试", desc: "最多重试 3 次，仍失败则通知你" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 6500,
    richCards: [{
      type: "deliverables", title: "可直接导入",
      files: [
        { name: "workflow.json", type: "json", size: "导入 n8n 即可使用" },
        { name: "runbook.md", type: "md", size: "运维手册" },
      ],
    }],
  },
  { type: "agent-reply", delay: 7800, content: "工作流设计好了：每周一早上自动从 Google Sheets 拉上周数据，汇总后发邮件给老板。如果中间出错会自动重试 3 次，全失败了会通知你处理。把 JSON 导入你的 n8n 就能用。" },
]

const BROWSER_USE_DEMO: SkillDemoStep[] = [
  { type: "user", content: "想买台半自动咖啡机，2000 以内，帮我比比价", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "打开京东", paramLabel: '搜索"半自动咖啡机"', result: "86 件" },
      { label: "打开淘宝", paramLabel: '搜索"半自动咖啡机"', result: "214 件" },
      { label: "打开拼多多", paramLabel: '搜索"半自动咖啡机"', result: "152 件" },
      { label: "提取数据", paramLabel: "各平台 TOP10 价格/评分", result: "30 条" },
    ],
  },
  {
    type: "rich-card", delay: 5500,
    richCards: [{
      type: "table", title: "比价表",
      columns: [
        { key: "model", label: "型号" },
        { key: "jd", label: "京东" },
        { key: "tb", label: "淘宝" },
        { key: "pdd", label: "拼多多" },
      ],
      rows: [
        { model: "德龙 ECP35.31", jd: "¥1,899", tb: "¥1,780", pdd: "¥1,699" },
        { model: "柏翠 PE3690", jd: "¥1,299", tb: "¥1,259", pdd: "¥1,199 ⚠️" },
        { model: "惠家 KD-130", jd: "¥1,580", tb: "¥1,520", pdd: "—" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 6800,
    richCards: [{
      type: "alert", title: "风险提示",
      items: [
        { level: "safe", label: "德龙 / 柏翠京东淘宝均为旗舰店" },
        { level: "warning", label: "柏翠拼多多店铺评分 4.2，非官方店" },
      ],
    }],
  },
  { type: "agent-reply", delay: 8000, content: "推荐「柏翠 PE3690」，淘宝旗舰店 ¥1,259 入手。帮你查了下有张满1200减80的券，领完到手 ¥1,179，比拼多多还便宜 20，售后也靠谱。" },
]

const OLLAMA_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我装一个本地的代码审查模型，不想把公司代码传到云上", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "列出本地模型", paramLabel: "当前已有 2 个模型", result: "已列出" },
      { label: "拉取模型", paramLabel: "qwen2.5-coder:7b（4.7GB）", result: "下载完成" },
      { label: "测试模型", paramLabel: "发送测试请求", result: "响应正常，1.2s" },
    ],
  },
  {
    type: "rich-card", delay: 4500,
    richCards: [{
      type: "table", title: "本地模型列表",
      columns: [
        { key: "model", label: "模型" },
        { key: "size", label: "大小" },
        { key: "usage", label: "用途" },
        { key: "status", label: "状态" },
      ],
      rows: [
        { model: "qwen3:4b", size: "2.6GB", usage: "通用对话", status: "已安装" },
        { model: "llama3.1:8b", size: "4.9GB", usage: "通用推理", status: "已安装" },
        { model: "qwen2.5-coder:7b", size: "4.7GB", usage: "代码审查", status: "刚装好" },
      ],
    }],
  },
  { type: "agent-reply", delay: 6000, content: "装好了，qwen2.5-coder:7b 专门优化过代码理解能力。所有推理都在你本地跑，代码不会上传到任何服务器。现在可以直接让我用它帮你审查代码了。" },
]

const ADAPTLYPOST_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我把这条产品发布的消息同时发到 Twitter、LinkedIn 和小红书，明天早上 9 点发", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "查询已绑定账号", paramLabel: "Twitter / LinkedIn / 小红书", result: "3 个账号在线" },
      { label: "创建帖子", paramLabel: "跨平台排期发布", result: "已排期" },
      { label: "设定时间", paramLabel: "明天 09:00 CST", result: "已设定" },
    ],
  },
  {
    type: "rich-card", delay: 4500,
    richCards: [{
      type: "table", title: "发布计划",
      columns: [
        { key: "item", label: "项目" },
        { key: "detail", label: "详情" },
      ],
      rows: [
        { item: "内容", detail: "我们的新产品正式上线了！..." },
        { item: "平台", detail: "Twitter · LinkedIn · 小红书" },
        { item: "排期", detail: "明天 09:00" },
        { item: "状态", detail: "已排期" },
      ],
    }],
  },
  { type: "agent-reply", delay: 6000, content: "帖子已排期，明天早上 9 点同步发到三个平台。发布后你可以回来问我看看各平台的互动数据。" },
]

const FINANCE_TRACKER_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我看看这个月花了多少钱，有没有超预算", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "读取本月账单", paramLabel: "3月1日-15日", result: "47 笔记录" },
      { label: "分类汇总", paramLabel: "按类别统计", result: "已汇总" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "table", title: "本月消费汇总",
      columns: [
        { key: "category", label: "类别" },
        { key: "amount", label: "金额" },
        { key: "budget", label: "预算" },
      ],
      rows: [
        { category: "餐饮", amount: "¥2,340", budget: "¥3,000" },
        { category: "房租水电", amount: "¥4,500", budget: "¥4,500" },
        { category: "购物", amount: "¥1,860", budget: "¥1,500" },
        { category: "交通", amount: "¥680", budget: "¥800" },
        { category: "娱乐", amount: "¥920", budget: "¥500" },
        { category: "合计", amount: "¥10,300", budget: "¥10,300" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 5200,
    richCards: [{
      type: "alert", title: "预算预警",
      items: [
        { level: "safe", label: "餐饮、交通在预算内" },
        { level: "warning", label: "购物超支 ¥360（超 24%）" },
        { level: "danger", label: "娱乐超支 ¥420（超 84%），主要是 3月8日游戏充值 ¥500" },
      ],
    }],
  },
  { type: "agent-reply", delay: 6500, content: "月过一半，总支出刚好卡在预算线上。但娱乐已经超了 84%，后半月建议控制一下。购物也超了一点，注意别再剁手了。" },
]

const LINEAR_DEMO: SkillDemoStep[] = [
  { type: "user", content: "这个 sprint 还有多少没做完的？", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "查询当前 Sprint", paramLabel: "Sprint 2026-W11", result: "已获取" },
      { label: "筛选未完成 Issue", paramLabel: "status ≠ Done", result: "8 条" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "table", title: "Sprint 进度",
      columns: [
        { key: "issue", label: "Issue" },
        { key: "assignee", label: "负责人" },
        { key: "priority", label: "优先级" },
        { key: "status", label: "状态" },
      ],
      rows: [
        { issue: "修复支付回调超时", assignee: "小王", priority: "紧急", status: "进行中" },
        { issue: "用户头像上传功能", assignee: "小李", priority: "中", status: "进行中" },
        { issue: "搜索结果分页", assignee: "小张", priority: "中", status: "待开始" },
        { issue: "API 限流策略", assignee: "—", priority: "低", status: "待分配" },
      ],
    }],
  },
  { type: "agent-reply", delay: 5500, content: "Sprint 还剩 8 条，其中「支付回调超时」最紧急，小王在处理了。API 限流还没人领，要我分配给谁吗？" },
]

const MEMORY_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我回忆一下上次我们讨论的技术选型结论", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "检索记忆", paramLabel: '"技术选型"', result: "3 条相关记忆" },
    ],
  },
  {
    type: "rich-card", delay: 3000,
    richCards: [{
      type: "deliverables", title: "相关记忆",
      files: [
        { name: "3月5日 — 前端框架决定用 Next.js，放弃 Nuxt", type: "memory" },
        { name: "3月5日 — 数据库用 PostgreSQL + Prisma ORM", type: "memory" },
        { name: "3月8日 — 部署方案改为 Vercel，放弃自建 Docker", type: "memory" },
      ],
    }],
  },
  { type: "agent-reply", delay: 4500, content: "上次讨论的结论是：Next.js + PostgreSQL + Prisma + Vercel 部署。3月8日还改了部署方案，从自建 Docker 切到了 Vercel。要我基于这套选型帮你搭项目脚手架吗？" },
]

const SENTRY_DEMO: SkillDemoStep[] = [
  { type: "user", content: "线上好像有用户反馈登录失败，帮我查查 Sentry 有没有相关报错", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "搜索 Sentry Issue", paramLabel: '"login" + "auth" 最近24h', result: "2 个 Issue" },
      { label: "获取 Issue 详情", paramLabel: "影响范围/堆栈", result: "已获取" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "table", title: "相关错误",
      columns: [
        { key: "error", label: "错误" },
        { key: "count", label: "频次" },
        { key: "users", label: "影响用户" },
        { key: "first", label: "首次出现" },
      ],
      rows: [
        { error: "TypeError: token is null", count: "342 次", users: "89 人", first: "今天 14:22" },
        { error: "504 Gateway Timeout /api/auth", count: "28 次", users: "15 人", first: "今天 15:01" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 5200,
    richCards: [{
      type: "alert", title: "诊断",
      items: [
        { level: "danger", label: "token is null", detail: "登录接口返回空 token，出现在 auth.ts:47，疑似上午的部署引入" },
        { level: "warning", label: "504 Timeout", detail: "认证服务响应超时，可能是上游数据库慢查询" },
      ],
    }],
  },
  { type: "agent-reply", delay: 6500, content: "找到了，主要问题是 token is null，影响 89 个用户，大概率是今天下午那次部署引入的。堆栈指向 auth.ts 第 47 行。要我帮你看看那段代码吗？" },
]

const SLACK_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我看看 #product 频道今天有什么重要消息，顺便跟小王说一声明天的会推迟到下午 3 点", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "读取频道消息", paramLabel: "#product 今日", result: "23 条消息" },
      { label: "发送私信", paramLabel: '@小王 "明天会议推迟到下午3点"', result: "已发送" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "deliverables", title: "#product 今日摘要",
      files: [
        { name: "小李（10:32）— 竞品分析报告已上传云盘，请大家看", type: "msg" },
        { name: "PM 老张（11:15）— Q2 路线图定稿了，需各组确认排期", type: "msg" },
        { name: "设计师阿花（14:20）— 新版首页设计稿已更新到 Figma", type: "msg" },
      ],
    }],
  },
  { type: "agent-reply", delay: 5500, content: "今天 #product 有 3 条重点：竞品报告、Q2 路线图定稿、新版设计稿更新。另外，已经私信小王通知会议推迟了。" },
]

const GITHUB_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我看看我有哪些 PR 等着 review", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "查询待 Review PR", paramLabel: "assigned as reviewer", result: "3 个 PR" },
    ],
  },
  {
    type: "rich-card", delay: 3000,
    richCards: [{
      type: "table", title: "待 Review 列表",
      columns: [
        { key: "pr", label: "PR" },
        { key: "repo", label: "仓库" },
        { key: "author", label: "作者" },
        { key: "changes", label: "改动" },
        { key: "waiting", label: "等待" },
      ],
      rows: [
        { pr: "#142 修复登录 token 问题", repo: "safeclaw-api", author: "小王", changes: "+12 -3", waiting: "2小时" },
        { pr: "#89 添加导出 PDF 功能", repo: "safeclaw-web", author: "小李", changes: "+340 -28", waiting: "1天" },
        { pr: "#23 更新依赖版本", repo: "infra-config", author: "bot", changes: "+8 -8", waiting: "3天" },
      ],
    }],
  },
  { type: "agent-reply", delay: 5000, content: "3 个 PR 等你 review。#142 修复登录 token 改动最小最紧急（和 Sentry 的问题可能有关），建议优先看。#23 是 bot 自动提的依赖更新，改动不大可以快速过。" },
]

const POSTGRESQL_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我查一下上个月注册的用户里，有多少人下过单", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "连接数据库", paramLabel: "safeclaw_prod (只读)", result: "已连接" },
      { label: "执行查询", paramLabel: "2月注册用户 × 订单表关联", result: "查询完成" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "table", title: "查询结果",
      columns: [
        { key: "metric", label: "指标" },
        { key: "value", label: "数量" },
      ],
      rows: [
        { metric: "2月新注册用户", value: "1,247 人" },
        { metric: "其中下过单的", value: "312 人" },
        { metric: "转化率", value: "25.0%" },
      ],
    }],
  },
  { type: "agent-reply", delay: 5500, content: "2 月注册了 1,247 人，其中 312 人下过单，转化率 25%。要我按注册渠道拆分看看哪个渠道质量最高吗？" },
]

const CONTEXT7_DEMO: SkillDemoStep[] = [
  { type: "user", content: "Next.js 15 的 Server Actions 怎么用？给我一个表单提交的例子", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "查找文档", paramLabel: "Next.js", result: "匹配到官方文档" },
      { label: "查询文档", paramLabel: '"Server Actions form submission"', result: "3 段相关内容" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "deliverables", title: "文档摘要 + 代码示例",
      files: [
        { name: "Server Actions 官方指南", type: "link", size: "nextjs.org/docs" },
        { name: "代码示例 — 表单提交 + 数据验证", type: "code" },
      ],
    }],
  },
  { type: "agent-reply", delay: 5500, content: 'Server Actions 在 Next.js 15 里直接用 "use server" 标记异步函数就行，不需要单独建 API 路由。表单提交的写法是把 action 函数传给 <form action={submitForm}>，代码示例已经给你了。这是当前最新文档的内容，不是过时版本。' },
]

const PLAYWRIGHT_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我测一下登录页面能不能正常工作", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "启动浏览器", paramLabel: "Chromium 无头模式", result: "已启动" },
      { label: "打开登录页", paramLabel: "localhost:3000/login", result: "页面加载" },
      { label: "填写表单", paramLabel: "输入邮箱/密码", result: "已填写" },
      { label: "点击登录", paramLabel: "提交表单", result: "跳转到首页" },
      { label: "验证结果", paramLabel: "检查欢迎文案", result: "测试通过" },
    ],
  },
  {
    type: "rich-card", delay: 5800,
    richCards: [{
      type: "table", title: "测试结果",
      columns: [
        { key: "testcase", label: "测试用例" },
        { key: "result", label: "结果" },
      ],
      rows: [
        { testcase: "页面正常加载", result: "通过" },
        { testcase: "输入表单并提交", result: "通过" },
        { testcase: "登录后跳转首页", result: "通过" },
        { testcase: "显示欢迎信息", result: "通过" },
      ],
    }],
  },
  { type: "agent-reply", delay: 7000, content: "登录流程全部正常——页面加载、表单提交、跳转、欢迎信息都没问题。要我再测测输错密码的情况吗？" },
]

const FIRECRAWL_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我把这个招聘网站上所有的产品经理岗位信息抓下来", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "抓取网页", paramLabel: "jobs.example.com/product-manager", result: "页面解析完成" },
      { label: "提取结构化数据", paramLabel: "职位名/公司/薪资/地点", result: "18 条数据" },
      { label: "导出文件", paramLabel: "CSV 格式", result: "已导出" },
    ],
  },
  {
    type: "rich-card", delay: 4500,
    richCards: [{
      type: "table", title: "采集结果",
      columns: [
        { key: "position", label: "职位" },
        { key: "company", label: "公司" },
        { key: "salary", label: "薪资" },
        { key: "location", label: "地点" },
      ],
      rows: [
        { position: "高级产品经理", company: "字节跳动", salary: "40-60K", location: "北京" },
        { position: "产品经理", company: "美团", salary: "30-50K", location: "上海" },
        { position: "AI 产品经理", company: "小米", salary: "35-55K", location: "北京" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 5800,
    richCards: [{
      type: "deliverables", title: "已导出",
      files: [
        { name: "产品经理岗位汇总.csv", type: "csv", size: "18 条记录" },
      ],
    }],
  },
  { type: "agent-reply", delay: 7000, content: "抓到 18 个产品经理岗位，已导出成 CSV。薪资最高的是字节跳动的高级产品经理（40-60K）。要我按薪资排序或者筛选特定城市吗？" },
]

const MARKITDOWN_DEMO: SkillDemoStep[] = [
  { type: "user", content: "客户发了一份 50 页的 PDF 合同，帮我提取里面的关键条款", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "解析 PDF 文件", paramLabel: "合同_v3.pdf（50页）", result: "转换完成" },
      { label: "提取关键条款", paramLabel: "付款/违约/终止", result: "12 条关键条款" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "deliverables", title: "提取结果",
      files: [
        { name: "合同关键条款摘要 — 12 条", type: "doc" },
        { name: "完整 Markdown 文本 — 合同_v3.md", type: "md" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 5000,
    richCards: [{
      type: "alert", title: "需注意条款",
      items: [
        { level: "danger", label: "第 8.3 条", detail: "违约金为合同总额的 30%，高于行业惯例（通常 10-20%）" },
        { level: "warning", label: "第 12.1 条", detail: "自动续约条款，不提前 60 天通知则自动续 1 年" },
        { level: "safe", label: "付款条件", detail: "验收后 30 天内付款，属于正常范围" },
      ],
    }],
  },
  { type: "agent-reply", delay: 6500, content: "合同里有 12 条关键条款。最需要注意的是违约金 30% 太高了，建议谈到 15% 以内。另外自动续约条款要记得设日历提醒。" },
]

const FEISHU_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我把这份周报发到飞书「产品组」群里，顺便 @ 一下老板", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "上传文件到飞书", paramLabel: "周报_W11.pdf", result: "已上传" },
      { label: "发送群消息", paramLabel: "产品组 + @老板", result: "已发送" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "table", title: "发送确认",
      columns: [
        { key: "item", label: "项目" },
        { key: "detail", label: "详情" },
      ],
      rows: [
        { item: "文件", detail: "周报_W11.pdf" },
        { item: "目标群", detail: "产品组" },
        { item: "@ 提醒", detail: "老板（张总）" },
        { item: "状态", detail: "已送达" },
      ],
    }],
  },
  { type: "agent-reply", delay: 5500, content: "周报已经发到「产品组」群里了，张总会收到 @ 提醒。下次可以让我每周五自动发。" },
]

const WHISPER_DEMO: SkillDemoStep[] = [
  { type: "user", content: "今天客户会议录了一个小时，帮我转成文字，整理出要点", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "加载音频文件", paramLabel: "客户会议_0315.m4a（58分钟）", result: "已加载" },
      { label: "语音识别转写", paramLabel: "Whisper 模型处理中", result: "12,400 字" },
      { label: "提取会议要点", paramLabel: "议题/决议/待办", result: "已提取" },
    ],
  },
  {
    type: "rich-card", delay: 4800,
    richCards: [{
      type: "deliverables", title: "转写结果",
      files: [
        { name: "完整会议纪要 — 12,400 字", type: "doc" },
        { name: "会议要点摘要 — 8 条", type: "doc" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 6000,
    richCards: [{
      type: "table", title: "会议要点",
      columns: [
        { key: "type", label: "类型" },
        { key: "content", label: "内容" },
        { key: "assignee", label: "负责人" },
      ],
      rows: [
        { type: "决议", content: "Q2 预算增加 15%", assignee: "财务部" },
        { type: "决议", content: "新功能 4 月底上线", assignee: "技术部" },
        { type: "待办", content: "发修订版报价单", assignee: "你" },
        { type: "待办", content: "安排下次评审会", assignee: "小王" },
      ],
    }],
  },
  { type: "agent-reply", delay: 7500, content: "58 分钟的录音已经转成文字了。关键决议是 Q2 预算加 15% 和新功能 4 月底上线。你有两个待办，最紧急的是发修订版报价单。" },
]

const ALIYUN_ASR_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我把这段客服电话录音转成文字，分析一下客户投诉了什么", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "上传音频", paramLabel: "客服录音_20260315.wav", result: "已上传" },
      { label: "阿里云 ASR 识别", paramLabel: "双声道分离（客服/客户）", result: "3,200 字" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "table", title: "通话摘要",
      columns: [
        { key: "speaker", label: "说话人" },
        { key: "content", label: "关键内容" },
      ],
      rows: [
        { speaker: "客户", content: "订单延迟 5 天未发货，要求退款" },
        { speaker: "客服", content: "解释因仓库系统升级导致延迟，承诺 24 小时内发货" },
        { speaker: "客户", content: "接受方案，但要求赠送运费险" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 5200,
    richCards: [{
      type: "alert", title: "服务质量",
      items: [
        { level: "safe", label: "客服态度评分：良好，全程礼貌" },
        { level: "warning", label: "问题解决：承诺了 24 小时发货，需跟进确认" },
        { level: "danger", label: "根因：仓库系统升级影响发货，需排查其他订单" },
      ],
    }],
  },
  { type: "agent-reply", delay: 6500, content: "客户投诉的是延迟发货，客服承诺了 24 小时内处理。建议你检查一下仓库系统升级是否还影响了其他订单，避免更多投诉。" },
]

const EMAIL_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我看看今天有没有重要邮件，然后给张总回一封确认参会的邮件", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "连接邮箱", paramLabel: "IMAP / your@company.com", result: "已连接" },
      { label: "拉取今日邮件", paramLabel: "筛选未读", result: "8 封未读" },
      { label: "发送邮件", paramLabel: "回复张总", result: "已发送" },
    ],
  },
  {
    type: "rich-card", delay: 4500,
    richCards: [{
      type: "deliverables", title: "今日重要邮件",
      files: [
        { name: "Q2 预算审批通过 — 来自 财务部，10:15", type: "email" },
        { name: "明天产品评审会议通知 — 来自 张总，11:30", type: "email" },
        { name: "新版 SDK 发布通知 — 来自 技术周报，14:00", type: "email" },
      ],
    }],
  },
  { type: "agent-reply", delay: 6000, content: "今天 8 封未读，3 封比较重要。已经帮你回复张总确认明天参会了。财务部那封好消息——Q2 预算审批通过了。" },
]

const BRIGHTDATA_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我采集竞品在各大电商平台的定价和评分数据", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "配置采集任务", paramLabel: "目标: 3 个竞品 × 3 个平台", result: "已配置" },
      { label: "采集数据", paramLabel: "价格/评分/销量", result: "27 条记录" },
      { label: "生成分析报告", paramLabel: "对比表", result: "已生成" },
    ],
  },
  {
    type: "rich-card", delay: 4500,
    richCards: [{
      type: "table", title: "竞品定价对比",
      columns: [
        { key: "product", label: "产品" },
        { key: "jd", label: "京东" },
        { key: "tmall", label: "天猫" },
        { key: "pdd", label: "拼多多" },
        { key: "rating", label: "平均评分" },
      ],
      rows: [
        { product: "竞品 A", jd: "¥299", tmall: "¥289", pdd: "¥269", rating: "4.7" },
        { product: "竞品 B", jd: "¥349", tmall: "¥339", pdd: "¥319", rating: "4.5" },
        { product: "竞品 C", jd: "¥199", tmall: "¥209", pdd: "¥179", rating: "4.3" },
        { product: "我们", jd: "¥329", tmall: "¥329", pdd: "¥329", rating: "4.6" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 6000,
    richCards: [{
      type: "alert", title: "定价建议",
      items: [
        { level: "danger", label: "拼多多比竞品 A 贵 ¥60，价格竞争力不足" },
        { level: "warning", label: "竞品 C 虽然便宜但评分最低，低价非核心威胁" },
        { level: "safe", label: "京东渠道与竞品 B 价差合理，评分领先" },
      ],
    }],
  },
  { type: "agent-reply", delay: 7500, content: "主要发现：我们拼多多定价偏高，建议针对该渠道单独定价或加大促销力度。其他渠道价格合理，评分在竞品中排第二。" },
]

const CODING_AGENT_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我给用户列表页加一个按注册时间排序的功能", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "分析项目结构", paramLabel: "Next.js + Prisma", result: "已分析" },
      { label: "定位相关文件", paramLabel: "users/page.tsx + api/users", result: "3 个文件" },
      { label: "编写代码", paramLabel: "排序参数 + UI 控件 + API 查询", result: "已完成" },
      { label: "运行测试", paramLabel: "3/3 通过", result: "全部通过" },
    ],
  },
  {
    type: "rich-card", delay: 5000,
    richCards: [{
      type: "table", title: "改动摘要",
      columns: [
        { key: "file", label: "文件" },
        { key: "changes", label: "改动" },
        { key: "desc", label: "说明" },
      ],
      rows: [
        { file: "users/page.tsx", changes: "+18 -2", desc: "添加排序下拉菜单" },
        { file: "api/users/route.ts", changes: "+8 -1", desc: "支持 sortBy 参数" },
        { file: "lib/queries.ts", changes: "+5 -1", desc: "Prisma orderBy 查询" },
      ],
    }],
  },
  { type: "agent-reply", delay: 6500, content: "排序功能加好了，改了 3 个文件。用户列表页右上角多了一个排序按钮，支持按注册时间正序/倒序。测试全部通过，你可以 pnpm dev 看看效果。" },
]

const SLIDES_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我做一份 Q2 产品路线图的 PPT，大概 8 页", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "设计大纲", paramLabel: "8 页结构", result: "已设计" },
      { label: "生成幻灯片", paramLabel: "封面/目标/时间线/里程碑/总结", result: "已生成" },
      { label: "导出文件", paramLabel: "Q2路线图.pptx", result: "已导出" },
    ],
  },
  {
    type: "rich-card", delay: 4500,
    richCards: [{
      type: "deliverables", title: "已生成",
      files: [
        { name: "Q2产品路线图.pptx — 8 页", type: "pptx" },
        { name: "演讲备注.md — 每页要点提示", type: "md" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 5500,
    richCards: [{
      type: "table", title: "内容大纲",
      columns: [
        { key: "page", label: "页码" },
        { key: "content", label: "内容" },
        { key: "type", label: "类型" },
      ],
      rows: [
        { page: "1", content: "封面：Q2 产品路线图", type: "标题页" },
        { page: "2", content: "Q1 回顾与关键数据", type: "数据图表" },
        { page: "3", content: "Q2 核心目标（3 个）", type: "要点列表" },
        { page: "4-6", content: "三大功能时间线", type: "甘特图" },
        { page: "7", content: "资源与风险", type: "表格" },
        { page: "8", content: "总结与下一步", type: "总结页" },
      ],
    }],
  },
  { type: "agent-reply", delay: 7000, content: "8 页 PPT 生成好了，包含 Q1 回顾、Q2 目标、时间线和风险分析。演讲备注也一并准备了，方便你对着讲。" },
]

const AI_IMAGE_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我生成一张产品发布会的宣传海报，科技感强一点", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "生成提示词", paramLabel: "科技风格 + 产品发布主题", result: "已生成" },
      { label: "AI 生成图像", paramLabel: "1024×1024", result: "已生成" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "deliverables", title: "生成结果",
      files: [
        { name: "宣传海报 v1 — 1024×1024 PNG", type: "image" },
        { name: "使用的提示词 — 可微调复用", type: "txt" },
      ],
    }],
  },
  { type: "agent-reply", delay: 5500, content: "海报生成好了，用了深蓝渐变底色 + 光线粒子效果突出科技感。如果想调整风格或加上你们的 Logo 和文案，告诉我具体内容。" },
]

const ELEVENLABS_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我把这段产品介绍文案转成语音，准备放到官网视频里", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "选择音色", paramLabel: "专业男声（中文）", result: "已选择" },
      { label: "生成语音", paramLabel: "280 字", result: "时长 42 秒" },
    ],
  },
  {
    type: "rich-card", delay: 3800,
    richCards: [{
      type: "deliverables", title: "语音文件",
      files: [
        { name: "产品介绍_配音.mp3 — 42 秒", type: "audio" },
        { name: "原文文案 — 280 字", type: "txt" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 5000,
    richCards: [{
      type: "table", title: "生成详情",
      columns: [
        { key: "item", label: "项目" },
        { key: "detail", label: "详情" },
      ],
      rows: [
        { item: "音色", detail: "专业男声（中文）" },
        { item: "时长", detail: "42 秒" },
        { item: "格式", detail: "MP3 / 44.1kHz" },
        { item: "字数", detail: "280 字" },
      ],
    }],
  },
  { type: "agent-reply", delay: 6500, content: "配音生成好了，42 秒的专业男声朗读。你可以先听听，不满意的话可以换音色或者调语速。" },
]

const APPLE_HEALTH_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我看看这周的运动和睡眠数据怎么样", delay: 0 },
  {
    type: "tool-calls", delay: 1200,
    toolCalls: [
      { label: "读取 Apple Health 数据", paramLabel: "本周运动/睡眠/心率", result: "已获取" },
    ],
  },
  {
    type: "rich-card", delay: 3000,
    richCards: [{
      type: "table", title: "本周健康概览",
      columns: [
        { key: "metric", label: "指标" },
        { key: "thisWeek", label: "本周平均" },
        { key: "lastWeek", label: "上周平均" },
        { key: "trend", label: "趋势" },
      ],
      rows: [
        { metric: "每日步数", thisWeek: "8,420 步", lastWeek: "7,200 步", trend: "+17%" },
        { metric: "运动时长", thisWeek: "45 分钟/天", lastWeek: "32 分钟/天", trend: "+41%" },
        { metric: "睡眠时长", thisWeek: "6h48m", lastWeek: "7h15m", trend: "-6%" },
        { metric: "静息心率", thisWeek: "62 bpm", lastWeek: "60 bpm", trend: "正常" },
      ],
    }],
  },
  {
    type: "rich-card", delay: 4500,
    richCards: [{
      type: "alert", title: "健康建议",
      items: [
        { level: "safe", label: "运动量提升明显，步数和运动时长都有增长" },
        { level: "danger", label: "睡眠时间减少了 27 分钟，连续 3 天不足 7 小时" },
        { level: "warning", label: "心率略升但在正常范围，可能与睡眠不足有关" },
      ],
    }],
  },
  { type: "agent-reply", delay: 6000, content: "运动方面进步很大，但睡眠在下滑——这周比上周平均少睡 27 分钟。运动量上去了睡眠也要跟上，不然容易疲劳积累。" },
]

// ── Demo registry: skill ID → demo steps ──
// Keys are exact skill IDs from the backend API

export const SKILL_DEMO_MAP: Record<string, SkillDemoStep[]> = {
  // 1: Obsidian
  obsidian: OBSIDIAN_DEMO,
  // 2: WHOOP
  whoop: WHOOP_DEMO,
  // 3: Summarize
  summarize: SUMMARIZE_DEMO,
  // 4: Notion
  notion: NOTION_DEMO,
  // 5: GoG
  gog: GOG_DEMO,
  // 6: Kalshi
  kalshi: KALSHI_DEMO,
  // 7: Mission Control
  "mission-control": MISSION_CONTROL_DEMO,
  // 8: Xerolite
  xerolite: XEROLITE_DEMO,
  // 9: Tavily
  tavily: TAVILY_DEMO,
  // 10: Skill Vetter
  "skill-vetter": SKILL_VETTER_DEMO,
  // 11: Fast.io
  "fast-io": FAST_IO_DEMO,
  // 12: n8n
  "n8n-workflow-automation": N8N_DEMO,
  // 13: Browser Use
  "browser-use": BROWSER_USE_DEMO,
  // 14: Ollama
  "ollama-local": OLLAMA_DEMO,
  // 15: AdaptlyPost
  adaptlypost: ADAPTLYPOST_DEMO,
  // 16: Finance Tracker
  "finance-tracker": FINANCE_TRACKER_DEMO,
  // 17: Linear
  linear: LINEAR_DEMO,
  // 18: Memory
  memory: MEMORY_DEMO,
  // 19: Sentry
  sentry: SENTRY_DEMO,
  // 20: Slack
  slack: SLACK_DEMO,
  // 21: GitHub
  github: GITHUB_DEMO,
  // 22: PostgreSQL
  postgres: POSTGRESQL_DEMO,
  // 23: Context7
  context7: CONTEXT7_DEMO,
  // 24: Playwright
  playwright: PLAYWRIGHT_DEMO,
  // 25: Firecrawl
  firecrawl: FIRECRAWL_DEMO,
  // 26: MarkItDown
  markitdown: MARKITDOWN_DEMO,
  // 27: 飞书 (two skill IDs)
  "feishu-file-sender": FEISHU_DEMO,
  "feishu-common": FEISHU_DEMO,
  // 28: Whisper
  "openai-whisper": WHISPER_DEMO,
  // 29: 阿里云 ASR
  "aliyun-asr": ALIYUN_ASR_DEMO,
  // 30: Email
  "imap-smtp-email": EMAIL_DEMO,
  // 31: BrightData
  brightdata: BRIGHTDATA_DEMO,
  // 32: Coding Agent
  "coding-agent": CODING_AGENT_DEMO,
  // 33: Slides
  "slides-cog": SLIDES_DEMO,
  // 34: AI 画图
  "ai-image-generation": AI_IMAGE_DEMO,
  // 35: ElevenLabs
  "elevenlabs-agents": ELEVENLABS_DEMO,
  // 36: Apple Health
  "apple-health-skill": APPLE_HEALTH_DEMO,
}

/** Fallback placeholder demo for skills without a dedicated script */
export const PLACEHOLDER_SKILL_DEMO: SkillDemoStep[] = [
  { type: "user", content: "帮我用这个技能整理一下我的笔记", delay: 0 },
  { type: "agent-reply", content: "好的，我来帮你整理笔记。让我先扫描一下你的笔记库结构...", delay: 1200 },
  { type: "agent-reply", content: "扫描完成！你的笔记库中共有 128 篇笔记，分布在 12 个文件夹中。我发现有 23 篇笔记没有分类标签，17 篇可能存在重复内容。", delay: 4000 },
  { type: "user", content: "先帮我处理重复的笔记吧", delay: 7500 },
  { type: "agent-reply", content: "去重完成！已合并 17 组重复笔记，保留了 128 篇中最完整的版本。笔记库现在有 111 篇独立笔记，结构更清晰了。", delay: 9000 },
]
